import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactElement } from 'react';
import { canViewPage } from '@cls/shared';
import { useAuth } from './features/auth/AuthProvider.js';
import { Shell } from './components/shell/Shell.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Login } from './pages/Login.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { SignPage } from './pages/SignPage.js';
import { Dashboard } from './pages/Dashboard.js';
import { Workers } from './pages/Workers.js';
import { Projects } from './pages/Projects.js';
import { Customers } from './pages/Customers.js';
import { Contacts } from './pages/Contacts.js';
import { Training } from './pages/Training.js';
import { Vacancy } from './pages/Vacancy.js';
import { Broadcasts } from './pages/Broadcasts.js';
import { AvailabilitySignalsPage } from './pages/AvailabilitySignals.js';
import { DeployLog } from './pages/DeployLog.js';
import { Suppliers } from './pages/Suppliers.js';
import { QrScanner } from './pages/QrScanner.js';
import { SignIn } from './pages/SignIn.js';
import { DocSign } from './pages/DocSign.js';
import { Settings } from './pages/Settings.js';
import { Users } from './pages/Users.js';
import { Compliance } from './pages/Compliance.js';
import { Calendar } from './pages/Calendar.js';
import { Timesheet } from './pages/Timesheet.js';
import { BulkDeploy } from './pages/BulkDeploy.js';
import { ScheduledReports } from './pages/ScheduledReports.js';

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}>
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/sign/:token" element={<SignPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const PAGES = ['dashboard', 'projects', 'workers', 'training', 'deploylog', 'calendar', 'availsignals', 'docsign', 'signin', 'vacancy', 'compliance', 'customers', 'contacts', 'suppliers', 'qrscan', 'broadcasts', 'timesheet', 'bulkdeploy', 'scheduledreports', 'settings', 'settings.users'];
  const landingPage = user ? (PAGES.find((p) => canViewPage(user, p)) ?? 'dashboard') : 'dashboard';
  const gate = (page: string, el: ReactElement): ReactElement => (user && canViewPage(user, page)) ? el : <Navigate to={`/${landingPage}`} replace />;

  if (window.location.pathname.startsWith('/sign/')) {
    return (
      <Routes>
        <Route path="/sign/:token" element={<SignPage />} />
      </Routes>
    );
  }

  return (
    <Shell>
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Navigate to={`/${landingPage}`} replace />} />
        <Route path="/dashboard" element={gate('dashboard', <Dashboard />)} />
        <Route path="/projects" element={gate('projects', <Projects />)} />
        <Route path="/customers" element={gate('customers', <Customers />)} />
        <Route path="/contacts" element={gate('contacts', <Contacts />)} />
        <Route path="/workers" element={gate('workers', <Workers />)} />
        <Route path="/training" element={gate('training', <Training />)} />
        <Route path="/vacancy" element={gate('vacancy', <Vacancy />)} />
        <Route path="/broadcasts" element={gate('broadcasts', <Broadcasts />)} />
        <Route path="/availsignals" element={gate('availsignals', <AvailabilitySignalsPage />)} />
        <Route path="/deploylog" element={gate('deploylog', <DeployLog />)} />
        <Route path="/suppliers" element={gate('suppliers', <Suppliers />)} />
        <Route path="/qrscan" element={gate('qrscan', <QrScanner />)} />
        <Route path="/signin" element={gate('signin', <SignIn />)} />
        <Route path="/docsign" element={gate('docsign', <DocSign />)} />
        <Route path="/settings" element={gate('settings', <Settings />)} />
        <Route path="/users" element={gate('settings.users', <Users />)} />
        <Route path="/compliance" element={gate('compliance', <Compliance />)} />
        <Route path="/calendar" element={gate('calendar', <Calendar />)} />
        <Route path="/timesheet" element={gate('timesheet', <Timesheet />)} />
        <Route path="/bulkdeploy" element={gate('bulkdeploy', <BulkDeploy />)} />
        <Route path="/scheduledreports" element={gate('scheduledreports', <ScheduledReports />)} />
        <Route path="*" element={<Navigate to={`/${landingPage}`} replace />} />
      </Routes>
      </ErrorBoundary>
    </Shell>
  );
}
